import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  IconButton,
  Checkbox,
  ListSubheader,
  ListItemText,
  ListItem,
  List,
  Pagination,
  Divider,
} from "@mui/material";
import CommonAccordion from "../../custom-components/CommonAccordion";
import { ReactComponent as LoadIcon } from "../../../images/loadExampleButton.svg";
import { ReactComponent as ImportIcon } from "../../../images/importIcon.svg";
import AddIcon from "@mui/icons-material/Add";
import { ReactComponent as AutomateIcon } from "../../../images/automateIcon.svg";
import FullscreenToggle from "../../custom-components/FullScreenToggle";
import DownloadIcon from "@mui/icons-material/Download";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import { ReactComponent as ExportButton } from "../../../images/exportButton.svg";
import ALMConnectionPage from "../../main-pages/input-pages/ALMConnection/ALMConnectionPage";
import { useLocation, useNavigate } from "react-router-dom";
import { testScape } from "../../../services/webServices";
import { LoadingModalContext } from "../../main-pages/ServicePage";
import TestPlanOuputComponent from "../../output-components/test-plan/TestPlanOutputComponent";
import SessionExpired from "../../popup-components/SessionExpiredPopup";
import { TestPlanContext } from "../../main-pages/TestPlanPage";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"; // Dropdown icon
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import MessagePopup from "../../popup-components/MessagePopup";
import { ReactComponent as GenerateButton } from "../../../images/generateButton.svg";

const testingToolCategories = {
  "UI/Functional Testing Tools & Frameworks": [
    "Selenium",
    "Cypress",
    "Appium",
    "Robot Framework",
    "Katalon Studio",
    "TestComplete",
    "Ranorex",
    "Telerik Test Studio",
    "Protractor",
    "Playwright",
    "TestNG",
    "JUnit",
    "Cucumber",
  ],
  "API Testing Tools & Frameworks": [
    "Postman",
    "SoapUI",
    "RestAssured",
    "Karate Framework",
    "Pact",
    "WireMock",
  ],
  "Performance Testing Tools & Frameworks": [
    "JMeter",
    "LoadRunner",
    "BlazeMeter",
    "Gatling",
    "Artillery",
    "NeoLoad",
  ],
  "Cross-Browser/Cloud Testing": [
    "BrowserStack",
    "Sauce Labs",
    "CrossBrowserTesting",
    "LambdaTest",
  ],
  "Test Management/Reporting": [
    "TestRail",
    "QTest",
    "Jira",
    "Azure DevOps",
    "TestLink",
    "Zephyr",
  ],
  "CI/CD and Automation Frameworks": [
    "GitLab CI/CD",
    "Jenkins",
    "Travis CI",
    "CircleCI",
    "TeamCity",
  ],
};

export default function TestPlanInputForm() {
  const location = useLocation();
  const {setLoadingState} = useContext(LoadingModalContext);
  const [selectPlanType, setSelectTestPlanType] = useState("");
  const [projectName, setProjectName] = useState("");
  const [sprintNumber, setSprintNumber] = useState("");
  const [sprintStartDate, setSprintStartDate] = useState("");
  const [sprintEndDate, setSprintEndDate] = useState("");
  const [testScope, setTestScope] = useState("");
  const [userStoryTitles, setUserStoryTitles] = useState([]); // Stores only titles
  const [selectedUserStories, setSelectedUserStories] = useState([]); // Stores full user story data
  const [functionalTestingType, setFunctionalTestingType] = useState("");
  const [apiTestingType, setApiTestingType] = useState("");
  const [databaseTestingType, setDatabaseTestingType] = useState("");
  const [automationTestingType, setAutomationTestingType] = useState("");
  const [selectedTools, setSelectedTools] = useState([]);
  const [errors, setErrors] = useState({});
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  const [environment, setEnvironment] = useState("");
  const [availabilityDate, setAvailabilityDate] = useState("");
  const [selectedTesters, setSelectedTesters] = useState([]);
  const [newTester, setNewTester] = useState("");
  const [endDateError, setEndDateError] = useState(false);

  const [openProjectPage, setOpenProjectPage] = useState(false);
  const [enableTestPlanOutput, setEnableTestPlanOutput] = useState(false);
  const [testPlanResponse, setTestPlanResponse] = useState({});
  const [sessionError, setSessionError] = useState(false);
  const [isAutomationDisabled, setIsAutomationDisabled] = useState(true);
  const [isTestingToolDisabled, setIsTestingToolDisabled] = useState(true);
  const [isOpen, setIsOpen] = useState(false); // Control dropdown visibility
  const [filteredTools, setFilteredTools] = useState(testingToolCategories);

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [storyToDeleteIndex, setStoryToDeleteIndex] = useState(null);

  const {
    setTestPlanIdentifier,
    setTestPlanTypes,
    setTestPlanEnvironment,
    setTestPlanOutput,
  } = useContext(TestPlanContext);

  const [visibleSteps, setVisibleSteps] = useState(1);
  const [accordionStates, setAccordionStates] = useState({
    testPlanIdentifier: true, // First one is open by default
    typesOfTesting: false,
    testEnvironment: false,
  });

  // Get all tool names as an array
  const allTools = Object.values(filteredTools).flat();

  // Handle selection
  const handleSelectionChange = (event) => {
    const value = event.target.value;
    if (value.includes("all")) {
      setSelectedTools(
        selectedTools.length === allTools.length ? [] : allTools
      );
    } else {
      setSelectedTools(value);
    }
  };

  const handleAddTester = () => {
    if (newTester.trim()) {
      const trimmedTester = newTester.trim();
      if (!selectedTesters.includes(trimmedTester)) {
        setSelectedTesters((prevTesters) => [...prevTesters, trimmedTester]);
      }
      setNewTester(""); // Clear input after adding
      setErrors({});
    }
  };

  // Check if "Select All" should be checked
  const isAllSelected = selectedTools.length === allTools.length;

  const convertToIST = (utcDate) => {
    if (!utcDate) return "";
    return new Date(utcDate).toLocaleDateString("en-CA", {
      timeZone: "Asia/Kolkata",
    }); // YYYY-MM-DD format
  };

  useEffect(() => {
    if (location.state) {
      setVisibleSteps(1);
      setEnableTestPlanOutput(false);
      setTestPlanIdentifier({});
      setTestPlanEnvironment({});
      setTestPlanOutput({});
      setTestPlanTypes({});
      setFunctionalTestingType("");
      setApiTestingType("");
      setDatabaseTestingType("");
      setAutomationTestingType("");
      setSelectedTools([]);
      setEnvironment("");
      setAvailabilityDate("");
      setSelectedTesters([]);
      setSelectTestPlanType("Sprint Test Plan");
      setProjectName(location.state?.projectName || "");
      setSprintNumber(location.state?.sprintDetails || "");
      setSprintStartDate(convertToIST(location.state?.startDate));
      setSprintEndDate(convertToIST(location.state?.endDate));
    }
  }, [location.state]);

  const importedUserStories = useMemo(
    () => location.state?.userStories || [],
    [location.state?.userStories]
  );

  // Memoize the transformation of user stories into numbered titles
  const numberedTitles = useMemo(() => {
    return importedUserStories.map((story, index) => `${story.Title}`);
  }, [importedUserStories]);

  // Generate full details for scope field
  const fullUserStoryDetails = useMemo(() => {
    return importedUserStories
      .map(
        (story, index) =>
          `${story.key}\nTitle: ${story.Title}\nDescription: ${story.Description}\nAcceptance Criteria: ${story["Acceptance Criteria"]}\nAdditional Information: ${story["Additional Information"]}`
      )
      .join("\n\n");
  }, [importedUserStories]);

  useEffect(() => {
    setUserStoryTitles(numberedTitles); // Store only titles
    setSelectedUserStories(importedUserStories); // Store full details
    setTestScope(fullUserStoryDetails); // Populate full details in scope
  }, [numberedTitles, fullUserStoryDetails, importedUserStories]);

  const handleRemoveTitle = (index) => {
    const updatedTitles = userStoryTitles.filter((_, i) => i !== index);
    const updatedStories = selectedUserStories.filter((_, i) => i !== index);

    setUserStoryTitles(updatedTitles);
    setSelectedUserStories(updatedStories);

    // Adjust the page if the current page is empty after deletion
    const totalPages = Math.ceil(updatedTitles.length / ITEMS_PER_PAGE);
    if (page > totalPages) {
      setPage(totalPages || 1); // Ensure the page is at least 1
    }

    // Update the scope with new details
    setTestScope(
      updatedStories
        .map(
          (story, i) =>
            `${story.key}\nTitle: ${story.Title}\nDescription: ${story.Description}\nAcceptance Criteria: ${story["Acceptance Criteria"]}\nAdditional Information: ${story["Additional Information"]}`
        )
        .join("\n\n")
    );
  };

  const handleTesterInputChange = (event) => {
    setNewTester(event.target.value);
  };

  // const handleTesterInputChange = (event) => {
  //   const value = event.target.value;
  //   setNewTester(value);
  //   const regex = /^[A-Za-z][A-Za-z. ]*$/; // Must start with an alphabet, followed by alphabets, dots, and spaces

  //   if (regex.test(value) || value === "") {
  //     setNewTester(value);
  //     setErrors((prev) => ({ ...prev, testers: "" })); // Clear error if valid
  //   } else {
  //     setErrors((prev) => ({
  //       ...prev,
  //       testers:
  //         "The name must start with a letter and can contain letters, dots, and spaces.",
  //     }));
  //   }
  // };

  const areAutomationFieldsFilled = () => {
    return automationTestingType && selectedTools;
  };

  const handleStartDateChange = (e) => {
    const startDate = e.target.value;
    setSprintStartDate(startDate);

    if (sprintEndDate && new Date(sprintEndDate) < new Date(startDate)) {
      setSprintEndDate("");
      setEndDateError(false);
    }
  };

  const handleEndDateChange = (e) => {
    const endDate = e.target.value;
    if (new Date(endDate) < new Date(sprintStartDate)) {
      setEndDateError(true);
    } else {
      setEndDateError(false);
      setSprintEndDate(endDate);
    }
  };


  const removeTester = (testerToRemove) => {
    setSelectedTesters((prev) =>
      prev.filter((tester) => tester !== testerToRemove)
    );
  };

  //Select Test Plan Type
  const handleSelectPlanTypeChange = (event) => {
    setSelectTestPlanType(event.target.value);
  };

  // Test Plan Identifier Load demo example
  const handleFormLoadDemoExample = (e) => {
    e.stopPropagation();
    setUserStoryTitles([]);
    setSelectTestPlanType("Sprint Test Plan");
    setProjectName("Agile Pilot Project 1");
    setSprintNumber("Sprint 1");
    setSprintStartDate("2025-03-03");
    setSprintEndDate("2025-03-14");
    setTestScope(
      "This user story covers the process of logging into Bitrix24, navigating to the Sales module, and creating an invoice successfully. It ensures that users can easily generate and track invoices in the system."
    );
  };

  // Types of Testing Load demo example
  const handleTypeLoadDemoExample = (e) => {
    e.stopPropagation();
    setFunctionalTestingType("Automated Testing");
    setApiTestingType("Yes");
    setDatabaseTestingType("Yes");
    setAutomationTestingType("End-to-End Testing");
    setSelectedTools(["Selenium"]);
  };

  // Test Environment Load demo example
  const handleEnvironmentLoadDemoExample = (e) => {
    e.stopPropagation();
    setEnvironment("SIT");
    setAvailabilityDate("2025-03-05");
    setSelectedTesters(["Automation Resource1", "Automation Resource2"]);
  };

  const isFormValid = () => {
    return (
      projectName.trim() !== "" &&
      sprintNumber.trim() !== "" &&
      sprintStartDate !== "" &&
      sprintEndDate !== "" &&
      testScope.trim() !== ""
    );
  };

  useEffect(() => {
    if (functionalTestingType === "Manual Testing") {
      setIsAutomationDisabled(true);
      setIsTestingToolDisabled(false);
      setAutomationTestingType("");
      setSelectedTools([]);
    }
    if (functionalTestingType === "Automated Testing") {
      setIsAutomationDisabled(false);
      setIsTestingToolDisabled(false);
    }
  }, [functionalTestingType]); // Runs only when functionalTestingType changes

  const isTypesValid = () => {
    // Case 1: Manual Testing
    if (functionalTestingType === "Manual Testing") {
      // For Manual Testing, we only need functional testing type and API testing type
      return (
        functionalTestingType &&
        apiTestingType &&
        databaseTestingType &&
        selectedTools.length > 0
      );
    }

    // Case 2: Automated Testing
    if (functionalTestingType === "Automated Testing") {
      // For Automated Testing, we need all fields filled
      return (
        functionalTestingType &&
        apiTestingType &&
        databaseTestingType &&
        selectedTools.length > 0 &&
        areAutomationFieldsFilled()
      );
    }

    // If neither case is met, return false
    return false;
  };

  // Function to update displayed tools based on selections
  const updateFilteredTools = (functionalType, apiType) => {
    if (functionalType === "Automated Testing") {
      // Show all tools if Automated Testing is selected
      setFilteredTools(testingToolCategories);
    } else if (functionalType === "Manual Testing") {
      // Start with Test Management/Reporting & Cross-Browser/Cloud Testing
      let filteredData = {
        "Test Management/Reporting":
          testingToolCategories["Test Management/Reporting"],
        "Cross-Browser/Cloud Testing":
          testingToolCategories["Cross-Browser/Cloud Testing"],
      };

      // Add API Testing Tools if API Testing is "Yes"
      if (apiType === "Yes") {
        filteredData["API Testing Tools & Frameworks"] =
          testingToolCategories["API Testing Tools & Frameworks"];
      }

      setFilteredTools(filteredData);
    }
  };

  // Handle Functional Testing Selection
  const handleFunctionalTestingChange = (event) => {
    const selectedType = event.target.value;
    setFunctionalTestingType(selectedType);
    updateFilteredTools(selectedType, apiTestingType);
  };

  // Handle API Testing Selection
  const handleAPITestingTypeChange = (event) => {
    const selectedType = event.target.value;
    setApiTestingType(selectedType);
    updateFilteredTools(functionalTestingType, selectedType);
  };

  const isEnvironmentValid = () => {
    return environment && availabilityDate && selectedTesters.length > 0;
  };

  // Test Plan Identifier Next
  const handleTestPlanNext = () => {
    if (isFormValid()) {
      setAccordionStates({
        ...accordionStates,
        testPlanIdentifier: false,
        typesOfTesting: true,
      });
      setVisibleSteps(2);
      setTestPlanIdentifier({
        projectName,
        sprintNumber,
        sprintStartDate,
        sprintEndDate,
        testScope,
      });
    }
  };

  // Types of Testing Next
  const handleTypesNext = () => {
    if (isTypesValid()) {
      setAccordionStates({
        ...accordionStates,
        typesOfTesting: false,
        testEnvironment: true,
      });
      setVisibleSteps(3);
      setTestPlanTypes({
        functionalTestingType,
        apiTestingType,
        automationTestingType,
        selectedTools,
      });
    }
  };

  const handleEnvironmentNext = async () => {
    setAccordionStates({
      ...accordionStates,
      testPlanIdentifier: false,
      testEnvironment: false,
    });
    if (isEnvironmentValid()) {
      setVisibleSteps(4);
      setTestPlanEnvironment({
        environment,
        availabilityDate,
        availabilityDate: availabilityDate,
        selectedTesters,
      });
      let fake;

      const startProgress = (setProgress) => {
        let progress = 0;
        let stop = false;
        let finalMessageShown = false;

        const interval = setInterval(() => {
          if (stop) return;

          progress += 0.75;
          if (progress >= 97 && !finalMessageShown) {
            finalMessageShown = true;
            setLoadingState((prev) => ({
              ...prev,
              message: "Almost done! Fetching your output...",
            }));
          }

          if (progress >= 97) progress = 97;

          setProgress((prev) => ({
            ...prev,
            progress: Math.max(prev.progress, Math.floor(progress)),
          }));
        }, 200);

        return {
          stop: () => {
            stop = true;
            clearInterval(interval);
          },
          completeTo100: async () => {
            stop = true;
            clearInterval(interval);

            // Rush to 100%
            setProgress((prev) => ({
              ...prev,
              progress: 100,
            }));
            await new Promise((res) => setTimeout(res, 500));
          },
        };
      };

      const finishTo100AndClose = async () => {
        await fake.completeTo100();
        setLoadingState({ open: false, message: "", progress: null });
      };
      // Initial message
      setLoadingState({
        open: true,
        message: "Generating Test Plan",
        progress: 0,
      });

      fake = startProgress(setLoadingState);

      try {
        const testScapeObject = {
          selectPlanType: selectPlanType,
          projectName: projectName,
          sprintNumber: sprintNumber,
          sprintStartDate: sprintStartDate,
          sprintEndDate: sprintEndDate,
          testScope: testScope,
          functionalTestingType: functionalTestingType,
          databaseTestingType: databaseTestingType,
          apiTestingType: apiTestingType,
          automationTestingType: automationTestingType,
          testingTools: selectedTools,
          environment: environment,
          availabilityDate: availabilityDate,
          testers: selectedTesters,
        };
        const testPlanData = await testScape(testScapeObject);
        if (Object.entries(testPlanData).length > 0) {
          setTestPlanResponse(testPlanData);
          setEnableTestPlanOutput(true);
          setTestPlanOutput(testPlanData);
          return;
        }
        await finishTo100AndClose();
      } catch (error) {
        const errorMsg = error?.response?.data?.message || "Something went wrong while generating the test plan.";
        setPopupMessage(errorMsg);
        setPopupOpen(true);

        if (errorMsg === "Your session has expired.") {
          setSessionError(true);
        }
        setLoadingState({ open: false, message: "", progress: null });
      } finally {
        setLoadingState({ open: false, message: "", progress: null });
      }
    } else {
      setErrors({
        ...errors,
        environment: !environment ? "Please select a test environment" : "",
        // apiTestingEnvironment: !apiTestingEnvironment
        //   ? "Please select whether API testing is required"
        //   : "",
        availabilityDate: !availabilityDate
          ? "Please select an availability date"
          : "",
        testers:
          selectedTesters.length === 0
            ? "Please select at least one tester"
            : "",
      });
    }
  };

  const ITEMS_PER_PAGE = 10;

  const [page, setPage] = useState(1);

  const handleChange = (event, value) => {
    setPage(value);
  };

  // Calculate the sliced data for the current page
  const startIndex = (page - 1) * ITEMS_PER_PAGE;
  const currentItems = userStoryTitles.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          bgcolor: "#f6f3f2",
          minHeight: "80vh",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
          overflowY: "auto",
          width: "100%",
          height: "70vh",
          position: "relative",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#888",
            borderRadius: "4px",
            minHeight: "20px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#555",
          },
        }}
      >
        <div
          style={{
            backgroundColor: "#F6F3F2",
            padding: "20px",
            mb: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: "601px",
                height: "auto",
                boxShadow: 0,
                transition: "all 0.3s ease",
                position: "relative",
                overflow: "hidden",
                bgcolor: "transparent",
                border: "none",
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                padding: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center", // Ensure vertical alignment is consistent
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-start"
                  sx={{
                    width: "100%",
                    textAlign: "left",
                    mb: 0.1,
                  }}
                >
                  <AutomateIcon style={{ marginRight: 6 }} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ mt: 0.5 }}
                  >
                    Generate a comprehensive test plan
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-end", // Aligns the FullscreenToggle button at the end vertically
                  }}
                >
                  <FullscreenToggle />
                </Box>
              </Box>
            </Box>

            {/* Test Plan Identifier Accordion */}
            <CommonAccordion
              summary={
                <Box display="flex" alignItems="center">
                  <Typography variant="subtitle1" sx={{ fontSize: "16px" }}>
                    Test Plan Identifier
                  </Typography>
                  {accordionStates.testPlanIdentifier && (
                    <Button
                      variant="text"
                      startIcon={<LoadIcon />}
                      sx={{
                        ml: 2,
                        textTransform: "none",
                        position: "relative",
                        top: "0px",
                        right: "-180px",
                      }}
                      onClick={handleFormLoadDemoExample}
                    >
                      Load Demo Example
                    </Button>
                  )}
                </Box>
              }
              details={
                <Box mb={2} sx={{ height: '500px', overflowY: "auto", scrollbarWidth: "thin", }}>
                  <Box mb={2} sx={{ width: "100%" }}>
                    <Box
                      sx={{
                        mt: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px dashed #49A3FF",
                        borderRadius: "8px",
                        padding: "14px",
                        cursor: "pointer",
                        width: "85%",
                        backgroundColor: "#F8FBFF",
                        color: "#000",
                        fontSize: "14px",
                        textAlign: "center",
                        fontWeight: 500,
                      }}
                      onClick={() => setOpenProjectPage(true)}
                    >
                      <ContentPasteSearchIcon sx={{ mr: 1 }} /> Import User
                      Stories for Sprint
                    </Box>

                    <Divider
                      sx={{
                        mt: 3,
                        color: "gray",
                        fontSize: "14px",
                        width: "85%", // Ensures it aligns with the box above
                      }}
                      variant="middle" // Centers the text within the divider
                    >
                      <Typography sx={{ color: "gray", fontSize: "14px" }}>
                        OR
                      </Typography>
                    </Divider>
                  </Box>

                  {/* Select Test Plan Type */}
                  <FormControl fullWidth sx={{ mt: 2, width: "92%" }}>
                    <InputLabel id="demo-simple-select-label">
                      Select Plan Type
                    </InputLabel>
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={selectPlanType}
                      label="Select Plan Type"
                      onChange={handleSelectPlanTypeChange}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 150,
                            overflowY: "auto",
                          },
                        },
                      }}
                    >
                      <MenuItem value="Sprint Test Plan">
                        Sprint Test Plan
                      </MenuItem>
                      <MenuItem value="Release Test Plan" disabled>
                        Release Test Plan
                      </MenuItem>
                      <MenuItem value="System test Plan" disabled>
                        System Test Plan
                      </MenuItem>
                      <MenuItem value="Integration Test Plan" disabled>
                        Integration Test Plan
                      </MenuItem>
                      <MenuItem value="UAT Test Plan" disabled>
                        UAT Test Plan
                      </MenuItem>
                      <MenuItem value="Master Test Plans" disabled>
                        Master Test Plan
                      </MenuItem>
                      <MenuItem value="Automation Test Plan" disabled>
                        Automation Test Plan
                      </MenuItem>
                      <MenuItem value="Security Test Plan" disabled>
                        Security Test Plan
                      </MenuItem>
                      <MenuItem value="Performance Test Plan" disabled>
                        Performance Test Plan
                      </MenuItem>
                      <MenuItem value="Accessibility Test Plan" disabled>
                        Accessibility Test Plan
                      </MenuItem>
                    </Select>
                  </FormControl>
                  {/* Project Name */}
                  <TextField
                    sx={{ width: "92%" }}
                    placeholder="Enter project name"
                    label="Project Name"
                    variant="outlined"
                    margin="normal"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                  />
                  {/* Sprint Number */}
                  <TextField
                    sx={{ width: "92%" }}
                    placeholder="Enter sprint number"
                    label="Sprint Number"
                    variant="outlined"
                    margin="normal"
                    value={sprintNumber}
                    onChange={(e) => setSprintNumber(e.target.value)}
                  />
                  {/* Start Date and End Date */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "92%",
                      gap: 2,
                    }}
                  >
                    <TextField
                      label="Sprint Start Date"
                      variant="outlined"
                      margin="normal"
                      type="date"
                      value={sprintStartDate}
                      onChange={handleStartDateChange}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: "50%" }}
                    />

                    <TextField
                      label="Sprint End Date"
                      variant="outlined"
                      margin="normal"
                      type="date"
                      value={sprintEndDate}
                      onChange={handleEndDateChange}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: sprintStartDate || "" }}
                      sx={{ width: "50%" }}
                      disabled={!sprintStartDate}
                      error={endDateError}
                      helperText={endDateError ? "End date cannot be earlier than start date" : ""}
                    />
                  </Box>

                  {/* Test Scope input field */}
                  {userStoryTitles.length <= 0 && (
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Scope"
                      placeholder="Provide user stories to be tested"
                      variant="outlined"
                      value={testScope}
                      onChange={(e) => setTestScope(e.target.value)}
                      sx={{
                        mt: 2,
                        width: "92%",
                        minHeight: "80px",
                        '& .MuiInputBase-root': {
                          // Ensure height behaves well
                          minHeight: '80px',
                          // Apply to the internal textarea's scrollbar
                          '& textarea': {
                            scrollbarWidth: 'thin', // Firefox
                            '&::-webkit-scrollbar': {
                              width: '6px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              backgroundColor: '#ccc',
                              borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-track': {
                              backgroundColor: '#f0f0f0',
                            },
                          },
                        },
                      }}
                    />
                  )}

                  {/* Imported User stories */}
                  {userStoryTitles.length > 0 && (
                    <Box
                      sx={{
                        mt: 2,
                        width: "92%",
                        border: "1px solid #ccc",
                        borderRadius: "8px",
                        p: 2,
                      }}
                    >
                      <Typography variant="body2">User Stories:</Typography>
                      <List>
                        {currentItems.map((title, index) => (
                          <ListItem
                            key={startIndex + index} // Unique key
                            secondaryAction={
                              <IconButton
                                edge="end"
                                aria-label="delete"
                                onClick={() => {
                                  setStoryToDeleteIndex(startIndex + index);
                                  setConfirmDialogOpen(true);
                                }}

                              >
                                <DeleteIcon />
                              </IconButton>
                            }
                          >
                            <ListItemText primary={title} />
                          </ListItem>
                        ))}
                      </List>

                      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
                        <DialogTitle>Confirm Deletion</DialogTitle>
                        <DialogContent>
                          <DialogContentText>
                            Are you sure you want to delete this user story?
                          </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                          <Button
                            color="#000"
                            onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
                          <Button
                            variant="contained"
                            sx={{ backgroundColor: "#1976d2", color: "white", '&:hover': { backgroundColor: '#1565c0' } }}
                            onClick={() => {
                              handleRemoveTitle(storyToDeleteIndex);
                              setConfirmDialogOpen(false);
                            }}
                            color="error"
                          >
                            Delete
                          </Button>
                        </DialogActions>
                      </Dialog>


                      {/* Pagination Controls & Count*/}
                      <Box
                        sx={{
                          position: "relative",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          mt: 2,
                        }}
                      >
                        {/* Left Side: Showing X/Y Items */}
                        <Typography
                          variant="body2"
                          sx={{
                            position: "absolute",
                            left: 0, // Aligns the count to the left
                          }}
                        >
                          {startIndex + 1}-
                          {Math.min(
                            startIndex + ITEMS_PER_PAGE,
                            userStoryTitles.length
                          )}{" "}
                          of {userStoryTitles.length} Items
                        </Typography>

                        {/* Centered Pagination */}
                        <Pagination
                          count={Math.ceil(
                            userStoryTitles.length / ITEMS_PER_PAGE
                          )}
                          page={page}
                          onChange={handleChange}
                          color="primary"
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              }
              buttons={[
                // {
                //   label: "Import",
                //   variant: "text",
                //   startIcon: <ImportIcon />,
                //   onClick: () => setOpenProjectPage(true),
                //   sx: {
                //     textTransform: "none",
                //     color: "#49A3FF",
                //   },
                // },
                {
                  label: "Next",
                  onClick: handleTestPlanNext,
                  variant: "contained",
                  startIcon: <GenerateButton />,
                  sx: {
                    float: "right",
                    textTransform: "none",
                    backgroundColor: isFormValid() ? "#49A3FF" : "grey",
                    color: isFormValid() ? "white" : "black",
                    "&:hover": {
                      backgroundColor: isFormValid() ? "#49A3FF" : "darkgrey",
                    },
                  },
                  disabled: !isFormValid(),
                },
              ]}
              // fullScreenMode={true}
              type="output"
              defaultExpanded={visibleSteps >= 2 ? false : true}
            />

            {/***** Types of Testing Accordion *****/}
            {visibleSteps >= 2 && (
              <CommonAccordion
                summary={
                  <Box display="flex" alignItems="center">
                    <Typography variant="subtitle1" sx={{ fontSize: "16px" }}>
                      Types of Testing
                    </Typography>
                    {accordionStates.typesOfTesting && (
                      <Button
                        variant="text"
                        startIcon={<LoadIcon />}
                        sx={{
                          ml: 2,
                          textTransform: "none",
                          position: "relative",
                          top: "0px",
                          right: "-190px",
                        }}
                        onClick={handleTypeLoadDemoExample}
                      >
                        Load Demo Example
                      </Button>
                    )}
                  </Box>
                }
                details={
                  <Box>
                    <Box mb={3} mt={2}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Functional Testing
                      </Typography>
                      <RadioGroup
                        row
                        value={functionalTestingType}
                        onChange={handleFunctionalTestingChange}
                      >
                        <FormControlLabel
                          value="Manual Testing"
                          control={<Radio />}
                          label="Manual Testing"
                          sx={{ mr: 10 }}
                        />
                        <FormControlLabel
                          value="Automated Testing"
                          control={<Radio />}
                          label="Automated Testing"
                        />
                      </RadioGroup>
                    </Box>

                    {/******* API Testing ********/}
                    <Box mb={3}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        API Testing
                      </Typography>
                      <RadioGroup
                        row
                        value={apiTestingType}
                        onChange={handleAPITestingTypeChange}
                      >
                        <FormControlLabel
                          value="Yes"
                          control={<Radio />}
                          label="Yes"
                          sx={{ mr: 10 }}
                        />
                        <FormControlLabel
                          value="No"
                          control={<Radio />}
                          label="No"
                        />
                      </RadioGroup>
                    </Box>

                    {/*****  Dtabase Testing *******/}
                    <Box mb={3}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Database Testing
                      </Typography>
                      <RadioGroup
                        row
                        value={databaseTestingType}
                        onChange={(e) => setDatabaseTestingType(e.target.value)}
                      >
                        <FormControlLabel
                          value="Yes"
                          control={<Radio />}
                          label="Yes"
                          sx={{ mr: 10 }}
                        />
                        <FormControlLabel
                          value="No"
                          control={<Radio />}
                          label="No"
                        />
                      </RadioGroup>
                    </Box>

                    {/* //Automation Testing Dropdwon */}
                    <Box mb={3}>
                      <FormControl
                        fullWidth
                        variant="outlined"
                        size="small"
                        sx={{
                          // width: "90%",
                          maxWidth: "830px",
                          height: "56px",
                          marginBottom: 3,
                          width: "90%",
                          backgroundColor: isAutomationDisabled
                            ? "#f5f5f5"
                            : "#E8F4FF",
                          "& .MuiSelect-select": {
                            color: automationTestingType ? "black" : "#999",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: isAutomationDisabled
                              ? "#e0e0e0"
                              : "#49A3FF",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: isAutomationDisabled
                              ? "#e0e0e0"
                              : "#49A3FF",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: isAutomationDisabled
                              ? "#e0e0e0"
                              : "#49A3FF",
                          },
                        }}
                        disabled={isAutomationDisabled}
                      >
                        <InputLabel
                          id="automation-test"
                          shrink
                          sx={{ background: "white", px: 1 }}
                        >
                          Automation Testing
                        </InputLabel>
                        <Select
                          labelId="automation-test"
                          value={automationTestingType}
                          onChange={(e) =>
                            setAutomationTestingType(e.target.value)
                          }
                          displayEmpty
                          fullWidth
                          disabled={isAutomationDisabled}
                          sx={{
                            height: "56px",
                            backgroundColor: isAutomationDisabled
                              ? "white"
                              : "white",
                            "& .MuiSelect-select": {
                              padding: "16px", // Adjust padding to align text
                              color: automationTestingType ? "black" : "#999",
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: isAutomationDisabled
                                ? "#e0e0e0"
                                : "#49A3FF",
                            },
                            // "&:hover .MuiOutlinedInput-notchedOutline": {
                            //   borderColor: isAutomationDisabled ? "#e0e0e0" : "#49A3FF",
                            // },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: isAutomationDisabled
                                ? "#e0e0e0"
                                : "#49A3FF",
                            },
                          }}
                          renderValue={(selected) =>
                            selected ? (
                              selected
                            ) : (
                              <span style={{ color: "#999" }}>
                                Select testing type
                              </span>
                            )
                          }
                        >
                          <MenuItem disabled value="">
                            <em>Select testing type</em>
                          </MenuItem>
                          <MenuItem value="Regression Testing">
                            Regression Testing
                          </MenuItem>
                          <MenuItem value="Performance Testing">
                            Performance Testing
                          </MenuItem>
                          <MenuItem value="Smoke Testing">
                            Smoke Testing
                          </MenuItem>
                          <MenuItem value="Sanity Testing">
                            Sanity Testing
                          </MenuItem>
                          <MenuItem value="End-to-End Testing">
                            End-to-End Testing
                          </MenuItem>
                          <MenuItem value="Acceptance Testing">
                            Acceptance Testing
                          </MenuItem>
                          <MenuItem value="UI Testing">UI Testing</MenuItem>
                          <MenuItem value="API Testing">API Testing</MenuItem>
                          <MenuItem value="Load Testing">Load Testing</MenuItem>
                          <MenuItem value="Stress Testing">
                            Stress Testing
                          </MenuItem>
                          <MenuItem value="Security Testing">
                            Security Testing
                          </MenuItem>
                          <MenuItem value="Exploratory Testing">
                            Exploratory Testing
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Testing Tool DropDown */}
                    <Box mb={3}>
                      <FormControl
                        fullWidth
                        variant="outlined"
                        size="small"
                        sx={{
                          marginBottom: 3,
                          width: "90%",
                          maxWidth: "830px",
                          backgroundColor: isTestingToolDisabled
                            ? "#f5f5f5"
                            : "#E8F4FF",
                          "& .MuiSelect-select": {
                            color: automationTestingType ? "black" : "#999",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: isTestingToolDisabled
                              ? "#e0e0e0"
                              : "#49A3FF",
                          },
                          // "&:hover .MuiOutlinedInput-notchedOutline": {
                          //   borderColor: isTestingToolDisabled
                          //     ? "#e0e0e0"
                          //     : "#49A3FF",
                          // },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: isTestingToolDisabled
                              ? "#e0e0e0"
                              : "#49A3FF",
                          },
                        }}
                        disabled={isTestingToolDisabled}
                      >
                        <InputLabel
                          id="automation-test"
                          shrink
                          sx={{ background: "white", px: 1 }}
                        >
                          Testing Tools
                        </InputLabel>
                        <Select
                          open={isOpen}
                          onOpen={() => setIsOpen(true)}
                          onClose={() => setIsOpen(false)}
                          displayEmpty
                          IconComponent={() => null}
                          labelId="testing-type-label"
                          multiple
                          value={selectedTools}
                          onChange={handleSelectionChange}
                          fullWidth
                          renderValue={(selected) =>
                            selected.length === 0 ? (
                              <span style={{ color: "#999" }}>
                                Select testing tools
                              </span>
                            ) : selected.length === 1 ? (
                              selected[0]
                            ) : (
                              `${selected[0]} (+${selected.length - 1})`
                            )
                          }
                          sx={{
                            backgroundColor: "white",
                            height: "56px",
                            "& .MuiSelect-select": {
                              padding: "16px",
                              color:
                                selectedTools.length > 0 ? "black" : "#999",
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#49A3FF",
                            },
                            // "&:hover .MuiOutlinedInput-notchedOutline": {
                            //   borderColor: "#49A3FF",
                            // },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#49A3FF",
                            },
                            display: "flex",
                            alignItems: "center",
                          }}
                          MenuProps={{
                            PaperProps: {
                              style: { maxHeight: 300, overflow: "auto" },
                            },
                          }}
                          endAdornment={
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              {/* Clear Selection Icon */}
                              {selectedTools.length > 0 && (
                                <IconButton
                                  onClick={(e) => {
                                    setSelectedTools([]);
                                  }}
                                  size="small"
                                  sx={{
                                    padding: 0, // Remove extra padding
                                  }}
                                >
                                  <ClearIcon
                                    fontSize="small"
                                    sx={{ marginRight: "2px" }}
                                  />
                                </IconButton>
                              )}

                              {/* Dropdown Icon (Manually Control Opening) */}
                              <IconButton
                                disabled={isAutomationDisabled}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setIsOpen((prev) => !prev); // Toggle dropdown state
                                }}
                                size="small"
                                sx={{
                                  padding: 0, // Remove extra padding
                                }}
                              >
                                <ArrowDropDownIcon
                                  fontSize="medium"
                                  sx={{ marginRight: "-8px" }}
                                />
                              </IconButton>
                            </Box>
                          }
                        >
                          {/* Default placeholder */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <MenuItem disabled value="">
                              <em>Select testing tools</em>
                            </MenuItem>
                            <Button
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent Select from reopening
                                setIsOpen(false); // Close the dropdown
                              }}
                            >
                              close
                            </Button>
                          </Box>

                          {/* Select All Checkbox */}
                          <MenuItem key="select-all" value="all">
                            <Checkbox checked={isAllSelected} />
                            Select All
                          </MenuItem>

                          {/* Render categories with subheadings */}
                          {Object.entries(filteredTools).map(
                            ([category, tools]) => [
                              <ListSubheader key={category}>
                                {category}
                              </ListSubheader>,
                              tools.map((tool) => (
                                <MenuItem key={tool} value={tool}>
                                  <Checkbox
                                    checked={selectedTools.includes(tool)}
                                  />
                                  {tool}
                                </MenuItem>
                              )),
                            ]
                          )}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                }
                buttons={[
                  {
                    label: "Next",
                    onClick: handleTypesNext,
                    startIcon: <GenerateButton />,
                    variant: "contained",
                    sx: {
                      float: "right",
                      textTransform: "none",
                      backgroundColor: isTypesValid() ? "#49A3FF" : "#e0e0e0",
                      color: isTypesValid() ? "white" : "#666",
                      "&:hover": {
                        backgroundColor: isTypesValid() ? "#3d8ddf" : "#d5d5d5",
                      },
                      padding: "8px 24px",
                    },
                    disabled: !isTypesValid(),
                  },
                ]}
                // fullScreenMode={true}
                type={"output"}
                defaultExpanded={visibleSteps >= 3 ? false : true}
              />
            )}
            {/* // Test Environment Accordion */}
            {visibleSteps >= 3 && (
              <CommonAccordion
                summary={
                  <Box display="flex" alignItems="center">
                    <Typography variant="subtitle1" sx={{ fontSize: "16px" }}>
                      Test Environment
                    </Typography>
                    {accordionStates.testEnvironment && (
                      <Button
                        variant="text"
                        startIcon={<LoadIcon />}
                        sx={{
                          ml: 2,
                          textTransform: "none",
                          position: "relative",
                          top: "0px",
                          right: "-190px",
                        }}
                        onClick={handleEnvironmentLoadDemoExample}
                      >
                        Load Demo Example
                      </Button>
                    )}
                  </Box>
                }
                details={
                  <Box>
                    <Box mb={3} mt={2}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Test Environment
                      </Typography>
                      <RadioGroup
                        row
                        value={environment}
                        onChange={(e) => setEnvironment(e.target.value)}
                      >
                        <FormControlLabel
                          value="JIT"
                          control={<Radio />}
                          label="JIT"
                          sx={{ mr: 6 }}
                        />
                        <FormControlLabel
                          value="SIT"
                          control={<Radio />}
                          label="SIT"
                          sx={{ mr: 6 }}
                        />
                        <FormControlLabel
                          value="UAT"
                          control={<Radio />}
                          label="UAT"
                        />
                      </RadioGroup>
                    </Box>

                    <Box mb={3}>
                      <TextField
                        label="QA/Test Environment Availability Date"
                        variant="outlined"
                        margin="normal"
                        type="date"
                        placeholder="mm-dd-yyyy"
                        value={availabilityDate}
                        onChange={(e) => setAvailabilityDate(e.target.value)}
                        sx={{ width: "100%", maxWidth: "505px" }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        inputProps={{
                          min: sprintStartDate
                            ? new Date(sprintStartDate)
                              .toISOString()
                              .split("T")[0]
                            : "",
                          max: sprintEndDate
                            ? new Date(sprintEndDate)
                              .toISOString()
                              .split("T")[0]
                            : "",
                        }}
                      />
                    </Box>

                    <Box sx={{ width: "90%" }}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "flex-start", // Align items at the top
                          mb: 2,
                        }}
                      >
                        <TextField
                          fullWidth
                          label="Add Testers"
                          value={newTester}
                          onChange={handleTesterInputChange}
                          placeholder="Type a name and click +"
                          error={errors.testers ? true : false}
                          helperText={errors.testers}
                        />
                        <IconButton
                          onClick={handleAddTester}
                          color="primary"
                          sx={{
                            border: "1px solid",
                            borderColor: "primary.main",
                            borderRadius: "50%",
                            width: 40,
                            height: 40,
                            marginTop: errors.testers ? "8px" : "8px", // Adjust icon position when error is shown
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        {selectedTesters.map((tester) => (
                          <Chip
                            key={tester}
                            label={tester}
                            onDelete={() => removeTester(tester)}
                            deleteIcon={
                              <ClearIcon
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeTester(tester);
                                }}
                              />
                            }
                            sx={{
                              backgroundColor: "#e8f4fd",
                              color: "#333",
                              "& .MuiChip-deleteIcon": {
                                color: "#999",
                              },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                }
                buttons={[
                  // {
                  //   label: "Download",
                  //   onClick: () => console.log("Download clicked"),
                  //   startIcon: <DownloadIcon />,
                  //   variant: "text",
                  //   sx: {
                  //     textTransform: "none",
                  //     color: "#49A3FF",
                  //   },
                  // },
                  // {
                  //   label: "Export",
                  //   onClick: () => console.log("Export clicked"),
                  //   startIcon: <ExportButton />,
                  //   variant: "text",
                  //   sx: {
                  //     textTransform: "none",
                  //     color: "#49A3FF",
                  //     ml: 2,
                  //   },
                  // },
                  {
                    label: "Next",
                    onClick: handleEnvironmentNext,
                    startIcon: <GenerateButton />,
                    variant: "contained",
                    sx: {
                      float: "right",
                      textTransform: "none",
                      backgroundColor: isEnvironmentValid()
                        ? "#49A3FF"
                        : "#e0e0e0",
                      color: isEnvironmentValid() ? "white" : "#666",
                      "&:hover": {
                        backgroundColor: isEnvironmentValid()
                          ? "#3d8ddf"
                          : "#d5d5d5",
                      },
                      padding: "8px 24px",
                      ml: "auto",
                    },
                    disabled: !isEnvironmentValid(),
                  },
                ]}
                type={"output"}
                defaultExpanded={visibleSteps >= 4 ? false : true}
              />
            )}
          </Box >
          {/* //OPEN SETTINGS PAGE */}
          {
            openProjectPage && (
              <ALMConnectionPage
                issueType={"Sprints"}
                setPageCloser={setOpenProjectPage}
              />
            )
          }

          {/* //ENABLE TEST PLAN OUTPUT */}
          {
            enableTestPlanOutput && (
              <TestPlanOuputComponent
                testPlanResponse={testPlanResponse}
                setTestPlanResponse={setTestPlanResponse}
              />
            )
          }

          {/* //Enable Session Error */}
          {sessionError && <SessionExpired />}

          {/**********    FOOTER ***********/}
          <Box
            component="footer"
            sx={{
              width: "100%",
              maxWidth: "601px",
              backgroundColor: "#f6f3f2",
              textAlign: "center",
              paddingX: "5px",
              position: "fixed",
              bottom: "0",
              zIndex: "99",
            }}
          >
            <Typography variant="caption" color="textSecondary">
              Use QMentisAI™ with human supervision, as with all generative AI
              tools.
            </Typography>
          </Box>
          <MessagePopup
            modalOpen={popupOpen}
            okayHandler={() => setPopupOpen(false)}
            message={popupMessage}
          />
        </div>
      </Box>
    </>
  );
}
